import { ChevronDown, ChevronRight, Pin, Star, Info } from 'lucide-react';
import { useNavigationStore } from '../../stores/navigationStore';
import { useLocation, Link } from 'react-router-dom';

interface NavItem {
  id: string;
  name: string;
  icon: React.ElementType;
  path: string;
  badge?: number;
  isNew?: boolean;
  /** Built, but with no live data source. Rendered greyed, with the reason as its tooltip. */
  unavailable?: string;
  /** Short pill text. Defaults to 'No feed' when a reason is present without one. */
  unavailableLabel?: string;
}

interface NavigationSectionProps {
  id: string;
  name: string;
  icon: React.ElementType;
  items: NavItem[];
  description?: string;
}

export function NavigationSection({ id, name, icon: SectionIcon, items, description }: NavigationSectionProps) {
  const location = useLocation();
  const {
    expandedSections,
    toggleSection,
    pinnedSections,
    togglePinSection,
    density,
    isFavorite,
    toggleFavorite,
    incrementUsage,
  } = useNavigationStore();

  const isExpanded = expandedSections.has(id) || pinnedSections.has(id);
  const isPinned = pinnedSections.has(id);
  const hasActiveItem = items.some((item) => location.pathname === item.path);

  const paddingClass = {
    compact: 'py-1.5 px-2',
    normal: 'py-2 px-3',
    comfortable: 'py-2.5 px-3',
  }[density];

  const spacingClass = {
    compact: 'space-y-0.5',
    normal: 'space-y-1',
    comfortable: 'space-y-1.5',
  }[density];

  const handleLinkClick = (item: NavItem) => {
    incrementUsage(item.id);
  };

  return (
    <div className={spacingClass}>
      <button
        onClick={() => toggleSection(id)}
        onContextMenu={(e) => {
          e.preventDefault();
          togglePinSection(id);
        }}
        className={`group flex w-full items-center justify-between rounded-lg ${paddingClass} transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
          hasActiveItem
            ? 'bg-blue-50 font-semibold text-blue-800'
            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950'
        }`}
        aria-expanded={isExpanded}
        aria-controls={`section-${id}`}
        title={description || `${name} section (right-click to pin)`}
      >
        <div className="flex items-center space-x-2 flex-1">
          <SectionIcon className="w-4 h-4" />
          <span className="text-sm font-medium">{name}</span>
          {description && (
            <Info className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition" />
          )}
        </div>
        <div className="flex items-center space-x-1">
          {isPinned && <Pin className="w-3 h-3 text-blue-500" />}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div
          id={`section-${id}`}
          className={`ml-4 border-l-2 border-gray-100 pl-3 ${spacingClass}`}
          role="group"
          aria-label={`${name} items`}
        >
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const itemIsFavorite = isFavorite(item.id);

            return (
              <div key={item.id} className="group/item relative flex items-center">
                <Link
                  to={item.path}
                  onClick={() => handleLinkClick(item)}
                  onContextMenu={(e) => {
                    // Only toggle favorite if not middle-click or ctrl+click (allow native "open in new tab")
                    if (!e.ctrlKey && !e.metaKey && e.button === 2) {
                      // Show custom context menu only on right-click without modifiers
                      // But we want to allow browser's native context menu for "Open in New Tab"
                      // So we don't prevent default here
                    }
                  }}
                  className={`flex-1 flex items-center space-x-2 ${paddingClass} rounded-lg border-l-2 transition text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
                    isActive
                      ? 'border-blue-600 bg-blue-100 text-blue-900 font-semibold shadow-sm'
                      : item.unavailable
                        // Greyed, but still a link. The pages hold real historical records
                        // and are not broken; what is missing is the feed. Hiding them would
                        // make a built capability look absent.
                        ? 'border-transparent text-slate-400 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-500'
                        : 'border-transparent text-slate-600 hover:border-slate-300 hover:bg-slate-100 hover:text-slate-950'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                  title={item.unavailable ? `${item.name} — ${item.unavailable}` : item.name}
                >
                  <Icon className={`w-4 h-4 ${item.unavailable ? 'opacity-60' : ''}`} />
                  <span className="flex-1 text-left truncate">{item.name}</span>
                  {item.unavailable && (
                    <span
                      className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-600 rounded"
                      // The reason travels with the badge, so the tooltip is not the only
                      // way to learn why the item is greyed.
                      title={item.unavailable}
                    >
                      {item.unavailableLabel ?? 'No feed'}
                    </span>
                  )}
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="px-1.5 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full min-w-[20px] text-center">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                  {/* A "New" badge on a feature with no live feed is a contradiction. */}
                  {item.isNew && !item.unavailable && (
                    <span className="px-1.5 py-0.5 text-xs font-semibold bg-green-500 text-white rounded">
                      New
                    </span>
                  )}
                </Link>
                <button
                  onClick={() => toggleFavorite(item.id)}
                  className={`absolute right-1 p-1 rounded transition focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    itemIsFavorite
                      ? 'text-yellow-500'
                      : 'text-gray-300 opacity-0 group-hover/item:opacity-100'
                  } hover:text-yellow-500`}
                  aria-label={itemIsFavorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <Star className={`w-3 h-3 ${itemIsFavorite ? 'fill-current' : ''}`} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}




























